import type { ReactNode } from "react";
import { X } from "@phosphor-icons/react";
import { useBodyScrollLock, useEscapeKey } from "../hooks/use-modal";

interface AuthModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Header mark. Deliberately a prop rather than a fixed `<BrandLockup />`:
   * the invite/SSO modals render the full logo image and the rest render the
   * lockup, and unifying them is a visual change, not a refactor.
   */
  brand?: ReactNode;
  children: ReactNode;
}

/**
 * Shared overlay + card + header chrome for the opero-web auth modals.
 *
 * Not built on `Dialog`: these modals sit at `z-[60]` to clear the navbar's
 * `z-50`, which Radix's hardcoded `z-50` content can't do, and the overlay
 * treatment (`bg-black/50 backdrop-blur-sm`) differs too.
 *
 * The shell stops at the header row — body padding diverges per modal, so
 * `children` supplies its own body wrapper.
 */
export function AuthModalShell({
  isOpen,
  onClose,
  brand,
  children,
}: AuthModalShellProps) {
  useBodyScrollLock(isOpen);
  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md mx-4 animate-scale-up">
        {/* `auth-surface` (index.css) upsizes the @opero/ui controls inside
            these modals from workspace density to the comfortable step this
            context wants — see the note there. */}
        <div className="auth-surface rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="flex items-center justify-between px-7 pt-6 pb-2">
            {brand}
            <button
              onClick={onClose}
              aria-label="Close"
              className="rounded-full p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
